import { create } from 'zustand';

// ============================================
// Verification Store — global modal state
// Triggered by: WelcomePage card, 403 interceptor, pending staff-pushed code
// ============================================

/** When set, the modal opens straight to code entry for an already-sent (staff-pushed) code. */
export interface PendingVerification {
  channel: string; // "WhatsApp" | "Sms"
  maskedPhone: string;
}

/**
 * Which OTP flow the modal drives:
 *  - 'session': verify the room phone for the whole session (first/primary verification).
 *  - 'device': "verify this device to order" — OTP to the already-on-file room phone, sets THIS
 *    device's TrustedAt. Only valid when the session is already PhoneVerified.
 */
export type VerificationMode = 'session' | 'device';

interface VerificationState {
  /** Whether the verification modal is visible */
  showModal: boolean;
  /** Optional callback to run after successful verification (e.g. retry the gated action) */
  onVerified: (() => void) | null;
  /** Set when a code was already sent (e.g. by the front desk) and just needs entering */
  pending: PendingVerification | null;
  /** Whether the modal verifies the session or just this device. Defaults to 'session'. */
  mode: VerificationMode;

  /** Open the verification modal. Optional callback runs after success. */
  openModal: (onVerified?: () => void, pending?: PendingVerification | null, mode?: VerificationMode) => void;
  /** Close modal without completing verification */
  closeModal: () => void;
  /** Called after OTP success — fires callback, closes modal */
  completeVerification: () => void;
}

export const useVerificationStore = create<VerificationState>((set, get) => ({
  showModal: false,
  onVerified: null,
  pending: null,
  mode: 'session',

  openModal: (onVerified, pending, mode) => {
    set({ showModal: true, onVerified: onVerified ?? null, pending: pending ?? null, mode: mode ?? 'session' });
  },

  closeModal: () => {
    set({ showModal: false, onVerified: null, pending: null, mode: 'session' });
  },

  completeVerification: () => {
    const { onVerified } = get();
    set({ showModal: false, onVerified: null, pending: null, mode: 'session' });
    // Fire callback after state update
    if (onVerified) {
      setTimeout(onVerified, 100);
    }
  },
}));
